// apps/web/src/features/cargo/components/CargoPaymentFinancierView.tsx
//
// Financier projection of the client-payment drawer (#1938): per active document
// an accept/reject control; on accept, a currency selector (RUB → receivedRub;
// USD/CNY → foreignAmount + exchangeRate + a read-only computed RUB preview). A
// running RUB total across accepted docs, and a Save that sends ONLY the changed
// decisions (never a no-op `pending`), honouring the conditional currency rules.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CARGO_PAYMENT_CURRENCIES,
  ErrorCode,
  type CargoPaymentCurrency,
  type CargoPaymentDocumentWire,
} from '@magimp/shared';

import { Button, Input, Select, Seg } from '../../../ui/index.js';
import { formatMoney } from '../../../i18n/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { ApiClientError, errorMessage } from '../../../api/errors.js';
import { useReviewCargoClientPayment } from '../mutations.js';
import {
  buildDecision,
  emptyReviewDraft,
  isDraftChanged,
  isDraftComplete,
  previewRubString,
  toMoneyString,
  type PaymentReviewDraft,
} from '../cargoPaymentReview.js';

// Inline-error mapping: each ErrorCode → a `cargo:payment.errors.*` leaf. Matched
// on the typed `ErrorCode`, NEVER on message text, so backend locale drift never
// shifts the UX. The leaf is composed into a template-literal i18n key on use.
type ReviewErrorLeaf =
  | 'reviewNotOpen'
  | 'reviewIncomplete'
  | 'documentLocked'
  | 'documentUnknown'
  | 'noActiveDocument';

const REVIEW_ERROR_LEAVES: Partial<Record<string, ReviewErrorLeaf>> = {
  [ErrorCode.CARGO_PAYMENT_REVIEW_NOT_OPEN]: 'reviewNotOpen',
  [ErrorCode.CARGO_PAYMENT_REVIEW_INCOMPLETE]: 'reviewIncomplete',
  [ErrorCode.CARGO_PAYMENT_DOCUMENT_LOCKED]: 'documentLocked',
  [ErrorCode.CARGO_PAYMENT_DOCUMENT_UNKNOWN]: 'documentUnknown',
  [ErrorCode.CARGO_PAYMENT_NO_ACTIVE_DOCUMENT]: 'noActiveDocument',
};

export interface CargoPaymentFinancierViewProps {
  cargoId: string;
  documents: readonly CargoPaymentDocumentWire[];
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
  onSaved: () => void;
}

export function CargoPaymentFinancierView({
  cargoId,
  documents,
  onDirtyChange,
  onBusyChange,
  onSaved,
}: CargoPaymentFinancierViewProps) {
  const { t } = useTranslation(['cargo']);
  const showToast = useToastStore((s) => s.show);
  const review = useReviewCargoClientPayment(cargoId);
  const [drafts, setDrafts] = useState<Record<string, PaymentReviewDraft>>({});
  const [inlineError, setInlineError] = useState<string | null>(null);

  const draftFor = (id: string): PaymentReviewDraft => drafts[id] ?? emptyReviewDraft();
  const patchDraft = (id: string, patch: Partial<PaymentReviewDraft>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...draftFor(id), ...patch } }));

  // Changed = a draft whose accept/reject differs from the persisted state.
  const changed = documents.filter((d) => isDraftChanged(draftFor(d.id), d));
  const dirty = changed.length > 0;
  // Save is blocked only when an accepted doc has empty/invalid amounts, OR
  // when there is nothing changed to send (the wire DTO requires min 1).
  const hasInvalidAccept = changed.some((d) => !isDraftComplete(draftFor(d.id)));
  const saveDisabled = review.isPending || !dirty || hasInvalidAccept;

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => onBusyChange(review.isPending), [review.isPending, onBusyChange]);
  useEffect(() => () => onBusyChange(false), [onBusyChange]);

  // Running RUB total across accepted drafts (RUB amount or the foreign
  // preview). Cheap loop over the visible docs — recomputed each render.
  let totalRub = 0;
  for (const doc of documents) {
    const draft = draftFor(doc.id);
    if (draft.decision !== 'accepted') continue;
    const rub =
      draft.currency === 'RUB'
        ? toMoneyString(draft.receivedRub)
        : previewRubString(draft.foreignAmount, draft.exchangeRate);
    if (rub != null) totalRub += Number(rub);
  }

  function onSave() {
    const decisions = changed
      .map((d) => buildDecision(d.id, draftFor(d.id)))
      .filter((d): d is NonNullable<typeof d> => d !== null);
    if (decisions.length === 0) return;
    setInlineError(null);
    review.mutate(
      { decisions },
      {
        onSuccess: () => {
          showToast({ tone: 'success', text: t('cargo:payment.review.toasts.saved') });
          setDrafts({});
          onSaved();
        },
        onError: (err) => {
          if (err instanceof ApiClientError) {
            const leaf = REVIEW_ERROR_LEAVES[err.code];
            if (leaf) {
              setInlineError(t(`cargo:payment.errors.${leaf}`));
              return;
            }
          }
          showToast({ tone: 'error', text: errorMessage(err) });
        },
      },
    );
  }

  if (documents.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {documents.map((doc) => (
        <DocDecisionRow
          key={doc.id}
          doc={doc}
          draft={draftFor(doc.id)}
          onPatch={(patch) => patchDraft(doc.id, patch)}
        />
      ))}
      <div
        data-testid="cargo-payment-review-total"
        style={{ fontSize: 13, color: 'var(--fg-1)', fontWeight: 600 }}
      >
        {t('cargo:payment.review.total', { amount: formatMoney(totalRub, 'RUB') })}
      </div>
      {inlineError && (
        <p
          role="alert"
          data-testid="cargo-payment-inline-error"
          style={{ margin: 0, fontSize: 12, color: 'var(--status-error-fg)' }}
        >
          {inlineError}
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          kind="primary"
          onClick={onSave}
          disabled={saveDisabled}
          data-testid="cargo-payment-review-save"
        >
          {t('cargo:payment.review.save')}
        </Button>
      </div>
    </div>
  );
}

function DocDecisionRow({
  doc,
  draft,
  onPatch,
}: {
  doc: CargoPaymentDocumentWire;
  draft: PaymentReviewDraft;
  onPatch: (patch: Partial<PaymentReviewDraft>) => void;
}) {
  const { t } = useTranslation(['cargo']);
  // `pending` carries no option, so an unreviewed draft simply matches nothing
  // in the Seg (no radio checked) — the financier must pick accept or reject.
  const decisionOptions: { value: PaymentReviewDraft['decision']; label: string }[] = [
    { value: 'accepted', label: t('cargo:payment.review.decision.accepted') },
    { value: 'rejected', label: t('cargo:payment.review.decision.rejected') },
  ];
  const preview =
    draft.currency === 'USD' || draft.currency === 'CNY'
      ? previewRubString(draft.foreignAmount, draft.exchangeRate)
      : null;

  return (
    <div
      data-testid="cargo-payment-review-row"
      data-doc-id={doc.id}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
      }}
    >
      <span
        style={{ fontSize: 13, color: 'var(--fg-1)', overflow: 'hidden', textOverflow: 'ellipsis' }}
        title={doc.fileName}
      >
        {doc.fileName}
      </span>
      <Seg<PaymentReviewDraft['decision']>
        options={decisionOptions}
        value={draft.decision}
        onChange={(decision) => onPatch({ decision })}
        aria-label={t('cargo:payment.review.decisionLabel')}
      />
      {draft.decision === 'accepted' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div data-testid="cargo-payment-review-currency">
            <Select
              value={draft.currency}
              onChange={(v) => onPatch({ currency: v as CargoPaymentCurrency })}
              options={CARGO_PAYMENT_CURRENCIES.map((c) => ({ value: c, label: c }))}
              placeholder={t('cargo:payment.review.currencyLabel')}
              style={{ width: '100%' }}
            />
          </div>
          {draft.currency === 'RUB' && (
            <AmountField
              label={t('cargo:payment.review.receivedRubLabel')}
              value={draft.receivedRub}
              onChange={(receivedRub) => onPatch({ receivedRub })}
              testId="cargo-payment-review-received-rub"
            />
          )}
          {(draft.currency === 'USD' || draft.currency === 'CNY') && (
            <>
              <AmountField
                label={t('cargo:payment.review.foreignAmountLabel')}
                value={draft.foreignAmount}
                onChange={(foreignAmount) => onPatch({ foreignAmount })}
                testId="cargo-payment-review-foreign-amount"
              />
              <AmountField
                label={t('cargo:payment.review.exchangeRateLabel')}
                value={draft.exchangeRate}
                onChange={(exchangeRate) => onPatch({ exchangeRate })}
                testId="cargo-payment-review-exchange-rate"
              />
              <span
                data-testid="cargo-payment-review-rub-preview"
                style={{ fontSize: 12, color: 'var(--fg-2)' }}
              >
                {t('cargo:payment.review.rubPreviewLabel')}:{' '}
                {formatMoney(preview != null ? Number(preview) : null, 'RUB')}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AmountField({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId: string;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{label}</span>
      <div data-testid={testId}>
        <Input value={value} onChange={onChange} ariaLabel={label} />
      </div>
    </label>
  );
}
